import styled from "@emotion/styled";
import { Button } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Container } from "@mui/system";
import Player from "@vimeo/player";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import { helpHttp } from "../../helper/helpHttp";
import { useUser } from "../../hooks/useUser";
import { toast } from "react-toastify";

// TODO: reemplazar por el id real del módulo en el CMS.
const MODULE_ID = 41;

// La API exige registrar 5 respuestas sí o sí, pero solo publicamos 1 pregunta.
// Las otras 4 son preguntas ocultas en el CMS, con texto "{{hidden_x}}", y les
// enviamos una respuesta fija para completar las 5 requeridas.
//
// En el CMS, 3 ocultas tienen solo la respuesta "correct" y 1 tiene "correct"
// e "incorrect". A cada oculta le enviamos "incorrect" si existe y si no
// "correct", así quedan 3 correctas + 1 incorrecta y la línea de aprobación
// del backend (4/5) cae en "1/1 visible":
//   1/1 visible -> 1 + 3 ocultas = 4/5 (aprueba)
//   0/1 visible -> 0 + 3 ocultas = 3/5 (no aprueba)
const HIDDEN_QUESTION_REGEX = /\{\{\s*hidden_\w+\s*\}\}/i;

// El CMS puede guardar el texto con HTML o espacios (&nbsp;), así que lo
// normalizamos antes de comparar.
const normalizarTexto = (texto) =>
  String(texto ?? "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const esPreguntaOculta = (item) =>
  HIDDEN_QUESTION_REGEX.test(normalizarTexto(item.pregunta));

const buscarRespuesta = (respuestas, texto) =>
  respuestas.find((r) => normalizarTexto(r.respuesta) === texto);

const respuestaOculta = (item) => {
  const respuestas = item.respuestas ?? [];
  const incorrecta = buscarRespuesta(respuestas, "incorrect");
  const respuesta = incorrecta ?? buscarRespuesta(respuestas, "correct");
  return {
    idPregunta: item.idPregunta,
    idRespuesta: respuesta?.idRespuesta,
    correcta: !incorrecta,
  };
};

const REQUIRED_ANSWER_COUNT = 5;

// Videos de Vimeo: 1 principal + 5 tips.
// thumbnail: URL de la miniatura en Cloudinary (16:9). Si está vacía se
// muestra un fondo neutro con ícono de play.
// TODO: completar las URLs de Cloudinary de las miniaturas.
const MAIN_VIDEO = { id: "1238398209", title: "Panettone" };
const TIPS_VIDEOS = [
  {
    id: "1238398207",
    title: "Tip de panadería 1",
    thumbnail:
      "https://res.cloudinary.com/ckvowh5m/image/upload/v1792728469/tip_1_MasVentas.jpg",
  },
  {
    id: "1238398206",
    title: "Tip de panadería 2",
    thumbnail:
      "https://res.cloudinary.com/ckvowh5m/image/upload/v1792728471/tip_2_TicketBajo.jpg",
  },
  {
    id: "1238398210",
    title: "Tip de panadería 3",
    thumbnail:
      "https://res.cloudinary.com/ckvowh5m/image/upload/v1792728476/tip_3_consumo.jpg",
  },
  {
    id: "1238398388",
    title: "Tip de panadería 4",
    thumbnail:
      "https://res.cloudinary.com/ckvowh5m/image/upload/v1792728472/tip_4_innovacion.jpg",
  },
  {
    id: "1238557078",
    title: "Tip de panadería 5",
    thumbnail:
      "https://res.cloudinary.com/ckvowh5m/image/upload/v1792728474/tip_5_Diferenciacion.jpg",
  },
];

export const Panettone = () => {
  const [preguntas, setPreguntas] = useState([]);
  const [respuestasOcultas, setRespuestasOcultas] = useState([]);
  const [idEvaluacion, setIdEvaluacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [estadoModulo, setEstadoModulo] = useState({
    yaAprobo: false,
    puedeReintentar: true,
    puedeReintentarEn24h: false,
    sinIntentos: false,
  });
  const [respuestas, setRespuestas] = useState({});
  const [resultado, setResultado] = useState(null);
  const [errorEnvio, setErrorEnvio] = useState(null);
  const [enviando, setEnviando] = useState(false);
  // null = video principal, número = índice en TIPS_VIDEOS
  const [videoActivo, setVideoActivo] = useState(null);
  const { post, get } = helpHttp();
  const { user } = useUser();
  const videoRef = useRef(null);
  const playerRef = useRef(null);

  const quizBloqueado = !estadoModulo.puedeReintentar && resultado === null;

  const actualizarEstadoModulo = (modulo) => {
    if (!modulo) return;
    const realizados = Number(modulo.intentos_realizados);
    const configurados = Number(modulo.intentos_configurados);
    const intentar = Number(modulo.intentar);
    setEstadoModulo({
      yaAprobo: String(modulo.aprobado) === "1",
      puedeReintentar: intentar === 1,
      puedeReintentarEn24h: realizados < configurados && intentar === 0,
      sinIntentos: realizados >= configurados,
    });
  };

  const cambiarVideo = (index) => {
    const video = index === null ? MAIN_VIDEO : TIPS_VIDEOS[index];
    setVideoActivo(index);
    playerRef.current
      ?.loadVideo(Number(video.id))
      .then(() => playerRef.current.play())
      .catch(() => {});
    videoRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleRespuesta = (preguntaIndex, opcionIndex) => {
    if (resultado || quizBloqueado) return;
    setRespuestas((prev) => ({ ...prev, [preguntaIndex]: opcionIndex }));
  };

  const handleEnviar = () => {
    if (quizBloqueado) return;
    if (Object.keys(respuestas).length < preguntas.length) return;

    const respuestasPayload = preguntas.map((p, i) => ({
      idPregunta: p.idPregunta,
      idRespuesta: p.opciones[respuestas[i]].idRespuesta,
    }));

    const payload = {
      idEvaluacion: idEvaluacion,
      respuestas: [
        ...respuestasPayload,
        ...respuestasOcultas.map((h) => ({
          idPregunta: h.idPregunta,
          idRespuesta: h.idRespuesta,
        })),
      ],
    };

    if (
      payload.respuestas.length !== REQUIRED_ANSWER_COUNT ||
      payload.respuestas.some((r) => !r.idRespuesta)
    ) {
      console.error("Evaluación Panettone mal configurada en el CMS", {
        esperadas: REQUIRED_ANSWER_COUNT,
        preguntasVisibles: preguntas,
        respuestasOcultas,
        payload,
      });
      toast.error(
        "No se pudo enviar la evaluación. Por favor intenta más tarde.",
      );
      return;
    }

    setEnviando(true);
    post("evaluaciones/registrarEvaluacion", { body: payload })
      .then((data) => {
        if (data?.codigo === "200") {
          setResultado(data.datos);
          get(`modulos/getModulosByIdAndUsuarioCampaign/${MODULE_ID}`, {
            headers: { Authorization: user.token },
          })
            .then((modData) => actualizarEstadoModulo(modData?.datos?.[0]))
            .catch(() => {});
        } else {
          setErrorEnvio(
            data?.mensaje ?? "Ha ocurrido un error al registrar la evaluación",
          );
        }
      })
      .catch((err) => {
        setErrorEnvio(
          err?.message ?? "Ha ocurrido un error al registrar la evaluación",
        );
      })
      .finally(() => setEnviando(false));
  };

  const handleReintentar = () => {
    setRespuestas({});
    setResultado(null);
    setErrorEnvio(null);
  };

  // resultado.exitosas incluye las respuestas ocultas que van correctas,
  // así que las restamos para mostrar solo los aciertos de la pregunta visible.
  const exitosasVisibles = resultado
    ? Math.max(
        0,
        Math.min(
          (resultado.exitosas ?? 0) -
            respuestasOcultas.filter((h) => h.correcta).length,
          preguntas.length,
        ),
      )
    : 0;

  useEffect(() => {
    playerRef.current = new Player(videoRef.current);

    const fetchModulo = get(
      `modulos/getModulosByIdAndUsuarioCampaign/${MODULE_ID}`,
      {
        headers: { Authorization: user.token },
      },
    ).then((data) => {
      const modulo = data?.datos?.[0];
      if (modulo) {
        if (modulo.evaluacion) setIdEvaluacion(modulo.evaluacion);
        actualizarEstadoModulo(modulo);
      }
    });

    const fetchPreguntas = get(
      `evaluaciones/getEvaluacionByModulo/${MODULE_ID}`,
      {
        headers: { Authorization: user.token },
      },
    ).then((data) => {
      if (data?.codigo === "200" && Array.isArray(data.datos)) {
        setRespuestasOcultas(
          data.datos.filter(esPreguntaOculta).map(respuestaOculta),
        );
        setPreguntas(
          data.datos
            .filter((item) => !esPreguntaOculta(item))
            .map((item) => ({
              idPregunta: item.idPregunta,
              pregunta: item.pregunta,
              opciones: item.respuestas,
            })),
        );
      }
    });

    Promise.allSettled([fetchModulo, fetchPreguntas])
      .then((results) => {
        if (results.some((r) => r.status === "rejected")) {
          toast.error("Ha ocurrido un error al cargar el contenido");
        }
      })
      .finally(() => setLoading(false));

    return () => {
      playerRef.current?.destroy().catch(() => {});
    };
  }, []);

  return (
    <StyledContainer>
      <Container maxWidth={false} sx={{ maxWidth: "1366px" }}>
        <StyledMain>
          <StyledVideo>
            <iframe
              ref={videoRef}
              width="100%"
              height="100%"
              src={`https://player.vimeo.com/video/${MAIN_VIDEO.id}`}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              title={MAIN_VIDEO.title}
              loading="lazy"
            />
          </StyledVideo>

          <StyledTipsHeader>
            <h2>Tips de panadería</h2>
            {videoActivo !== null && (
              <Button
                variant="text"
                onClick={() => cambiarVideo(null)}
                sx={{ color: "#773d1e" }}
              >
                Ver introducción
              </Button>
            )}
          </StyledTipsHeader>
          <StyledTips>
            {TIPS_VIDEOS.map((video, i) => (
              <StyledTip
                key={i}
                type="button"
                activo={videoActivo === i}
                onClick={() => cambiarVideo(i)}
              >
                {video.thumbnail ? (
                  <img src={video.thumbnail} alt={video.title} loading="lazy" />
                ) : (
                  <StyledTipPlaceholder aria-label={video.title}>
                    <PlayCircleOutlineRoundedIcon />
                  </StyledTipPlaceholder>
                )}
              </StyledTip>
            ))}
          </StyledTips>

          {loading ? (
            <StyledQuiz>
              <p>Cargando cuestionario...</p>
            </StyledQuiz>
          ) : estadoModulo.yaAprobo ? (
            <StyledAprobadoCard>
              <StyledAprobadoIcon color="green">✓</StyledAprobadoIcon>
              <h3>¡Ya aprobaste esta evaluación!</h3>
              <p style={{ margin: 0, color: "#773d1e", fontSize: "1.6rem" }}>
                Respondiste exitosamente, prepárate para ganar. !Mucha suerte!
              </p>
            </StyledAprobadoCard>
          ) : estadoModulo.sinIntentos ? (
            <StyledAprobadoCard>
              <StyledAprobadoIcon color="red">✕</StyledAprobadoIcon>
              <h3>Sin intentos disponibles</h3>
              <p style={{ margin: 0, color: "#773d1e", fontSize: "1.6rem" }}>
                Has utilizado todos los intentos permitidos para esta
                evaluación. Ya no es posible volver a realizarla.
              </p>
            </StyledAprobadoCard>
          ) : estadoModulo.puedeReintentarEn24h && resultado === null ? (
            <StyledAprobadoCard>
              <StyledAprobadoIcon color="orange">⏱</StyledAprobadoIcon>
              <h3>Intento en espera</h3>
              <p style={{ margin: 0, color: "#773d1e", fontSize: "1.6rem" }}>
                Podrás volver a intentar esta evaluación en las próximas 24
                horas. ¡Revisa el video y prepárate!
              </p>
            </StyledAprobadoCard>
          ) : (
            <StyledQuiz>
              <p style={{ margin: 0, color: "#114c97", fontSize: "1.6rem" }}>
                {/* TODO copy */}
                Mira los videos de <b>Panettone</b> y responde la evaluación.
              </p>
              <br />
              {preguntas.length === 0 ? (
                <p>No se pudo cargar el cuestionario.</p>
              ) : (
                <>
                  {preguntas.map((item, pi) => (
                    <StyledPregunta key={item.idPregunta}>
                      <p>
                        <strong>
                          {pi + 1}. {item.pregunta}
                        </strong>
                      </p>
                      <StyledOpciones>
                        {item.opciones.map((opcion, oi) => {
                          const seleccionada = respuestas[pi] === oi;
                          return (
                            <StyledOpcion
                              key={opcion.idRespuesta}
                              estado={seleccionada ? "seleccionada" : "default"}
                              onClick={() => handleRespuesta(pi, oi)}
                            >
                              <input
                                type="radio"
                                name={`pregunta-${pi}`}
                                checked={seleccionada}
                                readOnly
                              />
                              <strong>{String.fromCharCode(65 + oi)})</strong>
                              &nbsp;{opcion.respuesta}
                            </StyledOpcion>
                          );
                        })}
                      </StyledOpciones>
                    </StyledPregunta>
                  ))}

                  {errorEnvio ? (
                    <StyledResultado>
                      <p style={{ color: "#d32f2f" }}>
                        Ha superado el máximo número de intentos.
                      </p>
                    </StyledResultado>
                  ) : !resultado ? (
                    <Button
                      variant="contained"
                      onClick={handleEnviar}
                      disabled={
                        quizBloqueado ||
                        Object.keys(respuestas).length < preguntas.length ||
                        enviando
                      }
                      sx={{
                        alignSelf: "flex-start",
                        mt: 1,
                        backgroundColor: "#773d1e",
                        "&:hover": {
                          backgroundColor: "#b86e3a",
                        },
                      }}
                    >
                      {enviando ? "Enviando..." : "ENVIAR"}
                    </Button>
                  ) : (
                    <StyledResultado>
                      <p
                        style={{
                          fontSize: "1.4rem",
                          margin: "auto",
                          color: "#773d1e",
                        }}
                      >
                        Obtuviste <strong>{exitosasVisibles}</strong> de{" "}
                        <strong>{preguntas.length}</strong> respuestas
                        correctas.
                      </p>
                      <p
                        style={{
                          fontSize: "1.4rem",
                          margin: "auto",
                          color: "#773d1e",
                        }}
                      >
                        {resultado.aprobado
                          ? "¡Felicitaciones, aprobaste el cuestionario!"
                          : "No aprobaste el cuestionario."}
                      </p>
                      {resultado.reintentar && (
                        <Button variant="outlined" onClick={handleReintentar}>
                          Intentar nuevamente
                        </Button>
                      )}
                    </StyledResultado>
                  )}
                </>
              )}
            </StyledQuiz>
          )}
        </StyledMain>
        {/* TODO: habilitar cuando exista el PDF de términos y condiciones.
        <StyledTerminos>
          <a
            href="/images/lanzamientos/Terminosycondiciones-Panettone.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            Ver términos y condiciones
          </a>
        </StyledTerminos>
        */}
      </Container>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  background-color: #e5e5e5;
  min-height: calc(100vh - 64px);
  display: flex;
  align-items: center;
`;

const StyledMain = styled.div`
  padding-top: 120px;
  padding-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StyledVideo = styled.div`
  width: 100%;
  aspect-ratio: 16/9;
`;

const StyledQuiz = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  background: #fff;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

  h2 {
    margin: 0;
    font-size: 1.4rem;
    color: #222;
  }
`;

const StyledPregunta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  p {
    margin: 0;
    font-size: 1rem;
    color: #333;
  }
`;

const StyledOpciones = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const colorMap = {
  default: { bg: "#f5f5f5", border: "#ccc", text: "#333" },
  seleccionada: { bg: "#e3f2fd", border: "#1976d2", text: "#1976d2" },
};

const StyledOpcion = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: background 0.2s;
  background-color: ${({ estado }) =>
    colorMap[estado]?.bg ?? colorMap.default.bg};
  border: 1.5px solid
    ${({ estado }) => colorMap[estado]?.border ?? colorMap.default.border};
  color: ${({ estado }) => colorMap[estado]?.text ?? colorMap.default.text};

  input {
    accent-color: currentColor;
    cursor: pointer;
  }
`;

const StyledResultado = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  p {
    margin: 0;
    font-size: 1.1rem;
    color: #333;
  }
`;

const StyledAprobadoCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  background: #fff;
  border-radius: 12px;
  padding: 40px 32px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  text-align: center;

  h3 {
    margin: 0;
    font-size: 1.4rem;
    color: #333;
  }

  p {
    margin: 0;
    color: #555;
    font-size: 1rem;
  }
`;

const colorIconMap = {
  green: { bg: "#e8f5e9", text: "#2e7d32" },
  red: { bg: "#ffebee", text: "#c62828" },
  orange: { bg: "#fff3e0", text: "#e65100" },
};

const StyledAprobadoIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: ${({ color }) =>
    colorIconMap[color]?.bg ?? colorIconMap.green.bg};
  color: ${({ color }) => colorIconMap[color]?.text ?? colorIconMap.green.text};
  font-size: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
`;

const StyledTerminos = styled.div`
  padding: 16px 0 8px;
  text-align: center;

  a {
    color: #555;
    font-size: 0.85rem;
    text-decoration: underline;

    &:hover {
      color: #222;
    }
  }
`;

const StyledTipsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  h2 {
    margin: 0;
    font-size: 1.4rem;
    color: #773d1e;
  }
`;

const StyledTips = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StyledTip = styled("button", {
  shouldForwardProp: (prop) => prop !== "activo",
})`
  display: flex;
  padding: 0;
  background: #fff;
  border: 2px solid ${({ activo }) => (activo ? "#773d1e" : "transparent")};
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  text-align: left;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: border-color 0.2s;

  &:hover {
    border-color: #b86e3a;
  }

  img {
    width: 100%;
    aspect-ratio: 16/9;
    object-fit: cover;
    display: block;
    background: #ccc;
  }
`;

const StyledTipPlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 16/9;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3e3cf;
  color: #773d1e;

  svg {
    font-size: 2.5rem;
  }
`;
